import { Info,  Save as SaveIcon } from '@mui/icons-material';
import { Alert, Box, Fab, IconButton, MenuItem, Popover, Stack, Typography, useTheme } from "@mui/material";
import { isPrivateIP } from "range_check";
import { publish } from "pubsub-js";
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "react-query";
import { DeviceSubTypeEnum } from "../../common/enums/DeviceSubTypeEnum";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectFeatures2Enum } from "../../common/enums/ObjectFeatures2Enum";
import { PlatformObjectTypeEnum } from "../../common/enums/PlatformObjectTypeEnum";
import { TankLevelTransmitterTypeEnum } from "../../common/enums/TankLevelTransmitterTypeEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { ObjectChangedMsg, OBJECT_CHANGED_TOPIC } from "../../common/models/pub-sub/PubSubTopics";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useObjectAttributes } from "../../queries/object-attributes/UseObjectAttributes";
import { useObjectCommData } from "../../queries/object-properties/UseObjectCommData";
import { useObjectProperties } from "../../queries/object-properties/UseObjectProperties";
import { useObjectPropertyLists } from "../../queries/object-properties/UseObjectPropertyLists";
import { useSaveObjectProperties } from "../../queries/object-properties/UseSaveObjectProperties";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IObjectPropertiesDeviceModel, IObjectPropertiesTankBatteryModel, IObjectPropertiesTankModel, IObjectPropertiesWellModel, IObjectPropertyListModel } from "../../services/properties-data-service/Models";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import FormCustomAttributes, { FormCustomAttributesType } from "../forms/form-custom-attributes/FormCustomAttributes";
import FormInput from "../forms/form-input/FormInput";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import FormProperty from "../forms/form-property/FormProperty";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import LoadingZone from "../loading-zone/LoadingZone";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { CommObject } from "./CommObject";
import { defaultValues, mapToAttributes, mapToDeviceFormValues, mapToProperties, mapToTankBatteryFormValues, mapToTankFormValues, mapToWellFormValues, ObjectPropertiesFormValues, shapeCommDisplayData, shapeProtocolObjects } from "./ObjectPropertiesUtils";
import { ProtocolObject } from "./ProtocolObject";
import { BaseActionEnum } from '../action-menu/ActionMenuItem';
import _ from 'lodash';
import { useSaveAttributes } from '../../queries/attributes/UseSaveAttributes';
import { useGroup } from '../../queries/groups/UseGroup';
import ModifyPollingScheduleDialog, { bindModifyPollingScheduleDialog, useModifyPollingScheduleDialogState } from '../modify-polling-schedule-dialog/ModifyPollingScheduleDialog';
import { useTankStyles } from '../../queries/tank-styles/UseTankStyles';

export type ObjectPropertiesType = {
    executeAction: (action: BaseActionEnum | BaseActionEnum) => void;
};

export type ObjectPropertiesProps = {
    visible?: boolean;
};

function ObjectProperties({
    visible
}: ObjectPropertiesProps, ref: Ref<unknown> | undefined) {

    const [modifyPollingSchedule, setModifyPollingSchedule] = useState(false);
    const [popOverAnchorEl, setPopOverAnchorEl] = React.useState<HTMLButtonElement | undefined>();    
    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const { confirm, acknowledge } = useConfirmation();
    const [userState] = useUserContext();
    const customAttributes = useRef<FormCustomAttributesType>(undefined);

    const modifyPollingScheduleDialog = useModifyPollingScheduleDialogState();

    const objectType = routeContext.contextObject?.objectType ?? eLynxObjectTypeEnum.None;

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const { data: properties, 
            status: propertiesStatus,
        refetch: refetchProperties
    } = useQueryNotifications(useObjectProperties)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject
    }) as UseQueryResult<IObjectPropertiesDeviceModel |
                IObjectPropertiesTankModel |
                IObjectPropertiesTankBatteryModel |
                IObjectPropertiesWellModel | undefined>;

    const deviceProperties = objectType === eLynxObjectTypeEnum.Device ||
    objectType === eLynxObjectTypeEnum.Tank
    ? (properties as IObjectPropertiesDeviceModel)
    : undefined;

    const { data: propertyLists, status: listsStatus } = useQueryNotifications(useObjectPropertyLists)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject
    });

    const { data: attributes, status: attributesStatus, refetch: refetchAttributes } = useQueryNotifications(useObjectAttributes)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject
    });

    const { data: objectComms, status: objectCommsStatus } = useQueryNotifications(useObjectCommData)({
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        enabled: (deviceProperties?.hasCommEndpointView ?? false) && (deviceProperties?.platformObjectType === PlatformObjectTypeEnum.PollingServer),
        onError: () => {/* User Story 50643: Device Details - Suppress Oops Error when ACM cannot be contacted */ }
    });

    const { data: objectViewState, refetch: refectchViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: false
    });

    const { mutate: saveProperties } = useQueryNotifications(useSaveObjectProperties)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject,
        onSuccess: () => {
            acknowledge(t('properties.propertiesSaved'));
            refectchViewState();

            refetchProperties();
        }
    });


    const { mutate: saveAttributes } = useQueryNotifications(useSaveAttributes)({
        organizationID: group?.organizationID as number,        
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject,
        onSuccess: () => {
            acknowledge(t('properties.attributesSaved'));
            refetchAttributes();
        }
    });

    const {data: tankStyles} = useQueryNotifications(useTankStyles)({
        enabled: routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tank
    });


    useImperativeHandle<unknown, ObjectPropertiesType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );


    const isElynxUser = properties?.isElynxUser ?? false;
    const canModify = properties?.canModify ?? false;
    const allowVoiceAlarm = properties?.allowVoiceAlarm &&
        (objectType === eLynxObjectTypeEnum.Device ||
            objectType === eLynxObjectTypeEnum.Tank ||
            objectType === eLynxObjectTypeEnum.TankBattery);
    const hasCommEndpointView = deviceProperties?.hasCommEndpointView ?? false;
    const isPollingServerPlatform = deviceProperties?.platformObjectType === PlatformObjectTypeEnum.PollingServer;
    const hasGasComposition = deviceProperties?.hasGasComposition;
    const hasPollingSchedules = (deviceProperties?.tagPollingSchedules?.length ?? 0) > 0;
    const canModifyEndpointType = deviceProperties?.canModifyEndpointType ?? false;
    const deviceSubType = deviceProperties?.deviceSubType?.id;
    const active = objectType === eLynxObjectTypeEnum.TankBattery ? true : properties?.active ?? true;

    const allowExternalContent = ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.AdditionalContent) > 0;

    const showHybridServicesR1 = (
        objectType === eLynxObjectTypeEnum.Device &&
        hasCommEndpointView &&
        isPollingServerPlatform &&
        canModify);

    const protocolObjects = shapeProtocolObjects(objectComms);
    const commObjects = shapeCommDisplayData(protocolObjects);

    const form = useForm<ObjectPropertiesFormValues>({
        mode: "onChange",
        shouldFocusError: true,
        defaultValues: defaultValues
    });

    const {
        //control,
        handleSubmit,
        formState: {
            // errors,
            isDirty,
            isValid,
            //dirtyFields
        },
        getValues,
        // setValue,
        watch,
        trigger,
        reset,
    } = form;

    async function getUserConfirmation() {

        const ok = (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("properties.cancelPropertiesConfirmationDialogText", {objectType: eLynxObjectTypeEnum[routeContext?.contextObject?.objectType as eLynxObjectTypeEnum]}),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";

        if (ok) {
            reset();
        }
        return ok;
    }

    const onSubmit = () => {

        if (properties) {
            const formValues = getValues() as ObjectPropertiesFormValues;
            const updatedProperties = mapToProperties(properties, 
                        routeContext?.contextObject?.objectType as eLynxObjectTypeEnum, 
                        formValues, 
                        propertyLists as IObjectPropertyListModel);

            if(!_.isEqual(properties, updatedProperties)) {
                saveProperties(updatedProperties);    
            }

            const formAttributes = getValues('attributes');
            const updatedAttributes = mapToAttributes(formAttributes, attributes ?? []);

            if (updatedAttributes.length > 0) {
                saveAttributes(updatedAttributes);
            }

            if (updatedProperties.name !== properties?.name) {

                publish(OBJECT_CHANGED_TOPIC, {
                    entity: routeContext.contextObject,
                    name: updatedProperties.name
                } as ObjectChangedMsg);
            }
        }
    };

    const _validateUrlTitle = (value: string): boolean => {

        const title = getValues("externalContentTitle");
        const url = getValues("externalContentUrl");

        if ((url?.trim().length ?? 0) > 0) {
            return (title?.trim().length ?? 0) > 0;
        }

        return true;
    }
    const _validateUrl = (value: string): boolean => {

        const url = value;

        const title = getValues("externalContentTitle");
        if (url.trim().length === 0) {
            return (title?.trim().length ?? 0) === 0;
        }

        const arr = url?.split('/') ?? [];

        let addr = arr.length >= 3 ? arr[2] : '';

        // if address contains port number, strip it off before validating
        if (addr.indexOf(':') !== -1) {
            addr = addr.split(':')[0];
        }

        return isPrivateIP(addr) ?? false;
    };

    const [deviceTypeID, 
        levelTransmitterTypeID, 
        externalContentTitle, 
        externalContentUrl,
        //pollingScheduleID
    ] = watch([
        "deviceTypeID", 
        "levelTransmitterTypeID", 
        'externalContentTitle', 
        'externalContentUrl',
        //'pollingScheduleID'
    ]);

    const showExcludeBOPD = +deviceTypeID === 1000 &&
        (deviceSubType === DeviceSubTypeEnum.Oil || levelTransmitterTypeID === TankLevelTransmitterTypeEnum.DualPhase);
    const showExcludeBWPD = +deviceTypeID === 1000 &&
        (deviceSubType === DeviceSubTypeEnum.Water || levelTransmitterTypeID === TankLevelTransmitterTypeEnum.DualPhase);

    function executeAction(action: BaseActionEnum | BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.AddAttribute:
                customAttributes.current?.applyAttribute();
                break;

            case BaseActionEnum.ModifyPollingSchedule:
                onModifyPollingSchedule();
                break;

        }
    }
    

    async function onModifyPollingSchedule() {

        setModifyPollingSchedule(true);
        const model = properties as IObjectPropertiesDeviceModel | IObjectPropertiesTankModel;

        const result = await modifyPollingScheduleDialog.open({
            elynxObject: routeContext.contextObject as IeLynxObject,
            tagPollingSchedules: model.tagPollingSchedules
        });

        if (result.reason === 'save') {
            await refetchProperties();
        }

        setModifyPollingSchedule(false);

    }

    const onShowPopOver = (event: React.MouseEvent<HTMLButtonElement>) => {
        setPopOverAnchorEl(event.currentTarget);
      };
    
      const onClosePopOver = () => {
        setPopOverAnchorEl(undefined);
      };

    function onTankStyleChange() {
        
        const styleID = getValues("styleID");
        const style = tankStyles?.find(o => o.id === styleID);

        if (style) {

            form.setValue("barrelsPerInch", style.inchesMultiplier.toString());

        }
    }
    
    const open = Boolean(popOverAnchorEl);
    const id = open ? 'url-viewer-popover' : undefined;    

    useEffect(() => {
        if (properties && attributes) {

            switch (objectType) {
                case eLynxObjectTypeEnum.Device:
                    if (propertyLists) {
                        reset(mapToDeviceFormValues(properties as IObjectPropertiesDeviceModel, 
                                attributes,
                                propertyLists), 
                                {
                                    // This seems to be causing Bug #51762: Incorrect attributes displayed on tank/device details
                                    // keepDirtyValues: true
                                });
                    }
                    break;

                case eLynxObjectTypeEnum.Tank:
                    if (propertyLists) {
                        reset(mapToTankFormValues(properties as IObjectPropertiesTankModel, 
                            attributes,
                            propertyLists,
                        ), {
                            // This seems to be causing Bug #51762: Incorrect attributes displayed on tank/device details
                            // keepDirtyValues: true
                        });
                    }
                    break;

                case eLynxObjectTypeEnum.TankBattery:
                    reset(mapToTankBatteryFormValues(properties as IObjectPropertiesTankBatteryModel, attributes));
                    break;

                case eLynxObjectTypeEnum.Well:
                    reset(mapToWellFormValues(properties as IObjectPropertiesWellModel, attributes));
                    break;
                default:
                    break;
            }

        }

    }, [
        properties, 
        attributes, 
        propertyLists,
        objectType, 
        reset
    ]);



    useEffect(() => {

        if ((externalContentTitle?.trim().length === 0) && 
            (externalContentUrl?.trim().length ?? 0) === 0) {
            trigger(['externalContentTitle', 'externalContentUrl']);
        }

    }, [
        externalContentTitle,
        externalContentUrl,
        trigger
    ]);

    const standAloneTank = deviceProperties?.deviceType.id  === DeviceTypesEnum.StandaloneTank;
    const waterLeakDetection = deviceProperties?.deviceType.id === DeviceTypesEnum.WaterLeakDetection;
    const showDeviceType = !standAloneTank && !waterLeakDetection;
    
    return (
        <>
            {visible &&
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden"
                }}>
                    <LoadingZone isLoading={propertiesStatus === "loading" || attributesStatus === "loading" || listsStatus === "loading" || objectCommsStatus === "loading"}>
                        <Form data-testid="propertiesform" form={form} onSubmit={handleSubmit(async (data) => onSubmit())}>

                            {/* Standard Properties Section*/}
                            <FormPropertySection label={t("properties.standard")}>
                                <FormProperty>
                                    <FormInput
                                        id="name"
                                        name="name"
                                        label={t("properties.name")}
                                        rules={{
                                            required: true
                                        }}
                                        readOnly={!canModify}
                                        sx={{ 
                                            width: "100%",
                                            fontStyle: active ? 'normal' : 'italic',
                                         }}
                                    />
                                </FormProperty>

                                {allowVoiceAlarm &&
                                    <FormProperty>
                                        <FormInput
                                            id="phoneticname"
                                            name="phoneticName"
                                            label={t("properties.phoneticName")}
                                            rules={{
                                                required: false
                                            }}
                                            readOnly={!canModify}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                }

                                {objectType === eLynxObjectTypeEnum.Device &&
                                     showDeviceType &&
                                    <FormProperty>
                                        <FormSelect
                                            id="devicetype"
                                            name="deviceTypeID"
                                            label={t("properties.deviceType")}
                                            readOnly={!canModify}
                                            sx={{ width: "100%" }}
                                        >
                                            {propertyLists?.deviceTypes ? propertyLists.deviceTypes?.map((value, index) =>
                                                <MenuItem key={index} value={value.deviceTypeID}>
                                                    {value.deviceTypeName}
                                                </MenuItem>) :
                                                <MenuItem value={0}>
                                                    {''}
                                                </MenuItem>

                                            }
                                        </FormSelect>
                                    </FormProperty>
                                }

                                {(objectType === eLynxObjectTypeEnum.Device ||
                                    objectType === eLynxObjectTypeEnum.Tank ||
                                    objectType === eLynxObjectTypeEnum.TankBattery) &&
                                    <FormProperty>
                                        <FormInput
                                            id="identifier"
                                            name="identifier"
                                            label={t("properties.identifier")}
                                            readOnly={!canModify}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                }

                                {hasGasComposition &&
                                    <FormProperty>
                                        <FormCheckbox
                                            id="validategasanalysis"
                                            name="validateGasAnalysis"
                                            label={t("properties.validateGasAnalysis")}
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                }

                                {objectType === eLynxObjectTypeEnum.Well &&
                                    <>
                                        <FormProperty>
                                            <FormInput
                                                //id="masterid"
                                                name="padName"
                                                //ariaLabel="master identifier"
                                                label={t("properties.pad")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                        <FormProperty>
                                            <FormInput
                                                //id="masterid"
                                                name="fieldName"
                                                //ariaLabel="master identifier"
                                                label={t("properties.field")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                        <FormProperty>
                                            <FormInput
                                                //id="masterid"
                                                name="basinName"
                                                //ariaLabel="master identifier"
                                                label={t("properties.basin")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                        <FormProperty>
                                            <FormInput
                                                //id="masterid"
                                                name="apiID"
                                                //ariaLabel="master identifier"
                                                label={t("properties.api")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                        <FormProperty>
                                            <FormInput
                                                // id="masterid"
                                                name="liftTypeName"
                                                //ariaLabel="master identifier"
                                                label={t("properties.liftType")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                        <FormProperty>
                                            <FormInput
                                                id="masterid"
                                                name="resourceTypeName"
                                                ariaLabel="master identifier"
                                                label={t("properties.productType")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                    </>
                                }

                            </FormPropertySection>

                            {/* Hybrid Comm Section*/}

                            {showHybridServicesR1 &&
                                <FormPropertySection label={t("properties.commInfo")}>
                                    {commObjects?.map((item, index) =>
                                        <FormProperty key={index}>
                                            <CommObject item={item} />
                                        </FormProperty>
                                    )}

                                    {
                                    objectCommsStatus === 'error' &&
                                        <Alert severity='warning'
                                        sx={{
                                            marginTop: 1,
                                            marginBottom: 1,
                                            width: '100%'
                                        }}>
                                            {t('properties.noCommInfo')}
                                        </Alert>
                                    }

                                    {protocolObjects?.map((item, index) =>
                                        <FormProperty key={index}>
                                            <ProtocolObject item={item} />
                                        </FormProperty>
                                    )}
                                    {isElynxUser &&
                                        <FormProperty>
                                            <FormInput
                                                //id="masterids"
                                                name="commEndpointSource"
                                                //ariaLabel="master identifiers"
                                                label={t("properties.commEndpointSource")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                    }
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="iccid"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.simId")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="cycleToDateUsage"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.currentMonthUsage")}
                                            //helperText=" "
                                            readOnly={true}
                                            //rows={2}
                                            //multiline={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                </FormPropertySection>
                            }

                            {/* Legacy Comm Section */}
                            {!showHybridServicesR1 && canModifyEndpointType &&
                                <FormPropertySection label={t("properties.commInfo")}>
                                    <FormProperty>
                                        {/* TODO: Is this ever editable? If not, make FormInput                                     */}
                                        <FormSelect
                                            id="commendpointtype"
                                            name="commEndpointType"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.commEndpointType")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        >
                                            {propertyLists?.commEndpointTypes ? propertyLists.commEndpointTypes.map((value, index) =>
                                                <MenuItem key={index} value={value.value}>
                                                    {value.text}
                                                </MenuItem>) :
                                             <MenuItem value={0}>
                                                {''}
                                            </MenuItem>
                                            }

                                        </FormSelect>
                                    </FormProperty>

                                    <FormProperty>
                                        <FormInput
                                            //id="commendpointaddress"
                                            name="commEndpointAddress"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.commAddress")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="commAddressUpdated"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.commAddressUpdated")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="commEndpointID"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.commEndpointID")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                    {isElynxUser &&
                                        <FormProperty>
                                            <FormInput
                                                //id="masterids"
                                                name="commEndpointSource"
                                                //ariaLabel="master identifiers"
                                                label={t("properties.commEndpointSource")}
                                                //helperText=" "
                                                readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        </FormProperty>
                                    }
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="commEndpointUpdated"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.commEndpointUpdated")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="iccid"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.simId")}
                                            //helperText=" "
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="masterids"
                                            name="cycleToDateUsage"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.currentMonthUsage")}
                                            //helperText=" "
                                            readOnly={true}
                                            //rows={2}
                                            //multiline={true}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                </FormPropertySection>
                            }

                            {/* Geolocation Section */}
                            <FormPropertySection label={t("properties.geolocation")}>
                                {(objectType === eLynxObjectTypeEnum.Device ||
                                    objectType === eLynxObjectTypeEnum.Tank) &&
                                    <FormProperty>
                                        <FormInput
                                            //id="devicename"
                                            name="deviceLocation"
                                            //ariaLabel="device name"
                                            label={t("properties.geolocation")}
                                            //helperText=" "
                                            readOnly={!canModify}
                                            rules={{
                                                maxLength: 200,
                                                message: t('properties.maxLocationLength')
                                            }}
                                            sx={{ width: "100%" }}
                                        />
                                    </FormProperty>
                                }
                                <FormProperty>
                                    <FormInput
                                        // id="devicename"
                                        name="latitude"
                                        // ariaLabel="device name"
                                        label={t("properties.latitude")}
                                        //helperText=" "
                                        readOnly={!canModify}
                                        rules={{
                                            pattern: {
                                                value: /(^-90(\.0{0,1})?$)|(^-[0-8][0-9](\.[0-9]{0,15})?$)|(^-[0-9](\.[0-9]{0,15})?$)|(^-\.[0-9]{0,15}?$)|(^\+?90(\.0{0,15})?$)|(^\+?[0-8][0-9](\.[0-9]{0,15})?$)|(^\+?[0-9](\.[0-9]{0,15})?$)|(^\+?\.[0-9]{0,15}?$)/,
                                                message: t('properties.latitudeRange')
                                            }
                                        }}
                                        sx={{ width: "100%" }}
                                    />
                                </FormProperty>
                                <FormProperty>
                                    <FormInput
                                        //id="devicename"
                                        name="longitude"
                                        //ariaLabel="device name"
                                        label={t("properties.longitude")}
                                        //helperText=" "
                                        readOnly={!canModify}

                                        rules={{
                                            pattern: {
                                                value: /(^-180(\.0{0,15})?$)|(^-[0-1][0-7][0-9](\.[0-9]{0,15})?$)|(^-[0-9][0-9](\.[0-9]{0,15})?$)|(^-[0-9](\.[0-9]{0,15})?$)|(^-\.[0-9]{0,15}?$)|(^\+?180(\.0{0,15})?$)|(^\+?[0-1][0-7][0-9](\.[0-9]{0,15})?$)|(^\+?[0-9][0-9](\.[0-9]{0,15})?$)|(^\+?[0-9](\.[0-9]{0,15})?$)|(^\+?\.[0-9]{0,15}?$)/,
                                                message: t('properties.longitudeRange')
                                            }
                                        }}
                                        sx={{ width: "100%" }} />
                                </FormProperty>
                            </FormPropertySection>

                            {/* Tank properties section */}
                            {objectType === eLynxObjectTypeEnum.Tank &&
                                <FormPropertySection label={t("properties.tankProperties")}>
                                    <FormProperty>
                                        <FormSelect
                                            //id="masterids"
                                            name="levelTransmitterTypeID"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.tankLevelTransmitterType")}
                                            //helperText=" "
                                            readOnly={!canModify}
                                            sx={{ width: "100%" }}
                                        >
                                            {propertyLists?.tankLevelTransmitterTypes ? propertyLists?.tankLevelTransmitterTypes?.map((value, index) =>
                                                <MenuItem key={index} value={value.id}>
                                                    {value.description}
                                                </MenuItem>) :
                                            <MenuItem value={0}>
                                                {''}
                                            </MenuItem>
                                            }
                                        </FormSelect>
                                    </FormProperty>

                                    <FormProperty>
                                        <FormSelect
                                            //id="masterids"
                                            name="styleID"
                                            //ariaLabel="master identifiers"
                                            label={t("properties.tankStyle")}
                                            //helperText=" "
                                            readOnly={!canModify}
                                            sx={{ width: "100%" }}
                                            onChange={() => onTankStyleChange()}
                                        >
                                            {propertyLists?.tankStyles ? 
                                                propertyLists?.tankStyles?.filter(o => o.liquidProduction).map((value, index) =>
                                                <MenuItem key={index} value={value.id}>
                                                    {value.description}
                                                </MenuItem>) :
                                                <MenuItem value={0}>
                                                    {''}
                                                </MenuItem>
                                            }
                                        </FormSelect>
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="devicename"
                                            name="height"
                                            //ariaLabel="device name"
                                            label={t("properties.height")}
                                            //helperText=" "
                                            readOnly={true}
                                            // rules={{
                                            //     maxLength: 50,
                                            //     message: t("properties.maxDeviceNameLength")
                                            // }}
                                            sx={{ width: "100%" }} />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="devicename"
                                            name="barrelsPerInch"
                                            //ariaLabel="device name"
                                            label={t("properties.bblsPerInch")}
                                            //helperText=" "
                                            readOnly={true}
                                            // rules={{
                                            //     maxLength: 50,
                                            //     message: t("properties.maxDeviceNameLength")
                                            // }}
                                            sx={{ width: "100%" }} />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="devicename"
                                            name="capacity"
                                            //ariaLabel="device name"
                                            label={t("properties.capacity")}
                                            //helperText=" "
                                            readOnly={true}
                                            // rules={{
                                            //     maxLength: 50,
                                            //     message: t("properties.maxDeviceNameLength")
                                            // }}
                                            sx={{ width: "100%" }} />
                                    </FormProperty>

                                    <FormProperty>
                                        {showExcludeBOPD &&
                                            <FormCheckbox
                                                // id="devicetype"
                                                name="excludeBOPD"
                                                // ariaLabel="device type"
                                                label={t("properties.excludeFromOilCalc")}
                                                //helperText=" "
                                                //readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        }
                                        {showExcludeBWPD &&
                                            <FormCheckbox
                                                // id="devicetype"
                                                name="excludeBWPD"
                                                // ariaLabel="device type"
                                                label={t("properties.excludeFromWaterCalc")}
                                                //helperText=" "
                                                //readOnly={true}
                                                sx={{ width: "100%" }}
                                            />
                                        }
                                    </FormProperty>

                                </FormPropertySection>
                            }

                            {/* standalone Tank properties section */}
                            {standAloneTank &&
                                <FormPropertySection label={t("properties.tankProperties")}>

                                    <FormProperty>
                                        <FormSelect
                                            name="styleID"
                                            label={t("properties.tankStyle")}
                                            readOnly={!canModify}
                                            sx={{ width: "100%" }}
                                        >
                                            {propertyLists?.tankStyles ? 
                                                propertyLists?.tankStyles?.filter(o => !o.liquidProduction).map((value, index) =>
                                                <MenuItem key={index} value={value.id}>
                                                    {value.description}
                                                </MenuItem>) :
                                                <MenuItem value={0}>
                                                    {''}
                                                </MenuItem>
                                            }
                                        </FormSelect>
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            name="height"
                                            label={t("properties.height")}
                                            readOnly={true}
                                            sx={{ width: "100%" }} />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            name="resourceUnitsPerInch"
                                            label={deviceProperties?.resourceUnitsLabel}
                                            readOnly={true}
                                            sx={{ width: "100%" }} />
                                    </FormProperty>
                                    <FormProperty>
                                        <FormInput
                                            //id="devicename"
                                            name="capacity"
                                            //ariaLabel="device name"
                                            label={t("properties.capacity")}
                                            //helperText=" "
                                            readOnly={true}
                                            // rules={{
                                            //     maxLength: 50,
                                            //     message: t("properties.maxDeviceNameLength")
                                            // }}
                                            sx={{ width: "100%" }} />
                                    </FormProperty>

                                </FormPropertySection>
                            }
                            {/* Polling Schedule Section */}

                            {hasPollingSchedules && 
                                form.getValues()?.pollingScheduleID !== undefined &&
                                form.getValues()?.pollingScheduleID !== null &&
                                <FormPropertySection label={t("properties.pollingSchedule")}>
                                    {/* {`pollingscheduledid: ${form.getValues()?.pollingScheduleID}`} */}
                                    <FormProperty>
                                        <FormInput
                                            name='pollingScheduleName'
                                            label={t("properties.interval")}
                                            readOnly={true}
                                            sx={{ width: "100%" }}>

                                        </FormInput>

                                        {/* <FormSelect
                                            name="pollingScheduleID"
                                            label={t("properties.interval")}
                                            readOnly={true}
                                            sx={{ width: "100%" }}
                                        >
                                            {propertyLists?.tagPollingSchedules ? propertyLists?.tagPollingSchedules?.map((value, index) =>
                                                <MenuItem key={index} value={value.id}>
                                                    {value.description}
                                                </MenuItem>) :
                                                <MenuItem value={0}>
                                                    {''}
                                                </MenuItem>
                                            }
                                            <MenuItem key={propertyLists?.tagPollingSchedules.length} value={0}>
                                                Custom Per Tag
                                            </MenuItem>
                                        </FormSelect> */}
                                    </FormProperty>
                                </FormPropertySection>
                            }
                            {/* <FormPropertySection label={t("properties.images")}>
                        </FormPropertySection> */}

                            {
                                allowExternalContent && 

                                <FormPropertySection label={
                                    <Stack direction='row' spacing={1}>
                                        {t("properties.urlViewer")}
                                        <IconButton aria-label="info" 
                                            size="small"
                                            onClick={onShowPopOver}>
                                            <Info color='primary' 
                                                fontSize="inherit" 
                                            />
                                        </IconButton>
                                    </Stack>}>

                                    <Popover 
                                        id={id}
                                        open={open}
                                        anchorEl={popOverAnchorEl}
                                        onClose={onClosePopOver}
                                        anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                        }}
                                        sx={{maxWidth: 800}}
                                    >
                                        <Typography sx={{ p: 2, paddingBottom: 0 }} >{t('properties.additionalContentDisclaimer')}</Typography>
                                        <Typography sx={{ p: 2 }}>{t('properties.additionalContentDisclaimer2')}</Typography>
                                    </Popover>

                                    

                                    <FormProperty>
                                        <FormInput
                                        name={`externalContentTitle`}
                                        label={t("properties.urlTabName")}
                                        rules={{
                                            required: (externalContentUrl?.trim().length ?? 0) > 0,
                                            //maxLength: 128,
                                            //message: t('properties.maxUrlTabNameLength')
                                            validate: (value: string) => !_validateUrlTitle(value) ? 
                                                'URL Viewer Title is required'  :   
                                                value.trim().length > 128 ? 
                                                t('properties.maxUrlTabNameLength') : 
                                                true
                                        }}
                                        readOnly={!canModify}
                                        sx={{ width: "100%" }}/>
                                    </FormProperty>


                                    <FormProperty>
                                        <FormInput
                                        name={`externalContentUrl`}
                                        label={t("properties.url")}
                                        rules={{
                                            required: (externalContentTitle?.trim().length ?? 0) > 0,
                                            validate: (value: string) => !_validateUrl(value) ? 
                                                t('properties.urlPrivate') :   
                                                value.trim().length > 256 ? 
                                                    t('properties.maxUrlLength') : 
                                                    !_validateUrlTitle(value) ? 
                                                    'URL Viewer Title is required' :
                                                
                                                    true
                                        }}
                                        readOnly={!canModify}
                                        sx={{ width: "100%" }}/>
                                    </FormProperty>

                            </FormPropertySection>
            
                                
                            }


                            <FormCustomAttributes 
                                name='attributes' 
                                organizationID={group?.organizationID ?? -1}
                                groupID={routeContext.groupID as number}
                                ref={customAttributes}
                                objectType={routeContext.contextObject?.objectType as eLynxObjectTypeEnum}
                                readOnly={!canModify} 
                            />

                            {canModify &&

                                <Fab color="secondary"
                                    id="submit"
                                    type="submit"
                                    name="update"
                                    disabled={!isDirty || !isValid}
                                    aria-label="update properties" sx={{
                                        position: "absolute",
                                        bottom: theme.spacing(2),
                                        right: theme.spacing(1)
                                    }}>
                                    <SaveIcon />
                                </Fab>
                            }

                        </Form>
                    </LoadingZone>
                </Box>
            }
            {!modifyPollingSchedule && 
                <UnsavedChangesPrompt
                    hasChanges={isDirty}
                    getUserConfirmation={getUserConfirmation}
                />
            }
            <ModifyPollingScheduleDialog{...bindModifyPollingScheduleDialog(modifyPollingScheduleDialog)} />
        </>
    );
}

export default forwardRef(ObjectProperties);